import type {
  CreateFeedbackPayload,
  FeedbackAttachment,
  MyTicket,
  MyTicketDetail,
  RoutingResult,
  SupportDepartment,
  WidgetInfo,
} from "./model";

/**
 * Клиент плавающего «+» SMS-центра. Ходит в /api/feedback* — nginx отправляет их в Go
 * (go/retention/crm_feedback_proxy.go), а тот пробрасывает на CRM как есть. Сессия — cookie CRM
 * (fib.sid, domain=.brasol.ru), а НЕ JWT SMS-центра: общий `request` из ../api здесь нельзя —
 * на 401 он считает сессию SMS-центра протухшей, стирает токен и перезагружает страницу. Для «+»
 * 401 значит только «нет входа в CRM» (пользователь пришёл по SSO-токену без cookie) — экран просит
 * войти в CRM, остальное приложение не трогаем.
 */
export class FeedbackApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "FeedbackApiError";
  }
}

/** Нет входа в CRM: cookie сессии на sms-домене нет или она истекла. */
export const isCrmAuthError = (e: unknown): boolean =>
  e instanceof FeedbackApiError && e.status === 401;

const TIMEOUT_MS = 30_000;
const UPLOAD_TIMEOUT_MS = 60_000;

/** Текст ошибки: JSON CRM (`message` — строка или список) или обычный текст Go. */
export function errorMessage(status: number, text: string): string {
  try {
    const parsed = JSON.parse(text) as { message?: unknown };
    const m = parsed?.message;
    if (Array.isArray(m)) return m.map(String).join(" · ");
    if (m) return String(m);
  } catch {
    /* не JSON */
  }
  if (status === 429) return "Слишком много обращений — попробуйте позже";
  if (status === 413) return "Файл слишком большой";
  const plain = text.trim();
  if (plain && plain.length < 200 && !plain.startsWith("<")) return plain;
  return `Не удалось выполнить запрос (${status})`;
}

async function call<T>(
  method: "GET" | "POST",
  path: string,
  body?: unknown,
  opts: { signal?: AbortSignal; timeoutMs?: number } = {},
): Promise<T> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), opts.timeoutMs ?? TIMEOUT_MS);
  const onAbort = () => ctl.abort();
  if (opts.signal?.aborted) ctl.abort();
  opts.signal?.addEventListener("abort", onAbort);
  try {
    const isForm = typeof FormData !== "undefined" && body instanceof FormData;
    const res = await fetch(path, {
      method,
      credentials: "include",
      headers: body !== undefined && !isForm ? { "Content-Type": "application/json" } : {},
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
      signal: ctl.signal,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new FeedbackApiError(res.status, errorMessage(res.status, text));
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  } finally {
    clearTimeout(timer);
    opts.signal?.removeEventListener("abort", onAbort);
  }
}

export const feedbackApi = {
  widget: () => call<WidgetInfo>("GET", "/api/feedback/widget"),
  my: () => call<MyTicket[]>("GET", "/api/feedback/my"),
  myOne: (id: string) =>
    call<MyTicketDetail>("GET", `/api/feedback/my/${encodeURIComponent(id)}`),
  create: (p: CreateFeedbackPayload) =>
    call<{ id: string; code: string }>("POST", "/api/feedback", p),
  comment: (id: string, text: string) =>
    call<unknown>("POST", `/api/feedback/${encodeURIComponent(id)}/comments`, { text }),
  attach: (id: string, file: Blob, filename: string, kind: "screenshot" | "file") => {
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file, filename);
    return call<FeedbackAttachment>(
      "POST",
      `/api/feedback/${encodeURIComponent(id)}/attachments`,
      fd,
      { timeoutMs: UPLOAD_TIMEOUT_MS },
    );
  },
  /** «Кому уйдёт вопрос». Ничего не сохраняет; `signal` — отмена устаревшего запроса. */
  routePreview: (body: { text: string; deptCode?: string }, signal?: AbortSignal) =>
    call<RoutingResult>("POST", "/api/feedback/route-preview", body, { signal }),
  departments: () =>
    call<SupportDepartment[]>("GET", "/api/feedback/support/departments"),
};
